import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Settings → Appearance (Figma 09 · Form pages / 09). Kept per device: the
 * backend has no user preferences, so "on every device you sign in to" is not
 * possible yet. The rail's theme toggle writes the same store.
 */
export type ThemePreference = "system" | "light" | "dark";

interface AppearanceStore {
  theme: ThemePreference;
  reduceMotion: boolean;
  solidSurfaces: boolean;
  setTheme: (theme: ThemePreference) => void;
  setReduceMotion: (on: boolean) => void;
  setSolidSurfaces: (on: boolean) => void;
}

/**
 * Settings M19: the whole product cross-fades to the other theme at once. The
 * browser snapshots the old frame, the new theme renders in one synchronous
 * step with transitions off (cockpit.css `data-theme-switching`), and the two
 * cross-fade on the compositor, so the cost no longer scales with the page.
 * Without view transitions (or with reduced motion) it simply switches.
 */
function crossFadeTheme(apply: () => void) {
  const root = document.documentElement;
  const done = () => root.removeAttribute("data-theme-switching");
  root.setAttribute("data-theme-switching", "");
  const reduced =
    root.dataset.coMotion === "reduce" || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced || typeof document.startViewTransition !== "function") {
    flushSync(apply);
    requestAnimationFrame(() => requestAnimationFrame(done));
    return;
  }
  document.startViewTransition(() => flushSync(apply)).finished.then(done, done);
}

export const useAppearanceStore = create<AppearanceStore>()(
  persist(
    (set, get) => ({
      // Dark was the product's only default before this page existed.
      theme: "dark",
      reduceMotion: false,
      solidSurfaces: false,
      setTheme: (theme) => {
        if (get().theme === theme) return;
        crossFadeTheme(() => set({ theme }));
      },
      setReduceMotion: (reduceMotion) => set({ reduceMotion }),
      setSolidSurfaces: (solidSurfaces) => set({ solidSurfaces }),
    }),
    { name: "co-appearance" },
  ),
);

const DARK_QUERY = "(prefers-color-scheme: dark)";

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia(DARK_QUERY).matches;
  } catch {
    return true;
  }
}

/** The theme actually painted: System follows the device, live. */
export function useResolvedTheme(): "light" | "dark" {
  const theme = useAppearanceStore((s) => s.theme);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (theme !== "system") return;
    let media: MediaQueryList;
    try {
      media = window.matchMedia(DARK_QUERY);
    } catch {
      return;
    }
    const onChange = () => setSystemDark(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [theme]);

  if (theme === "system") return systemDark ? "dark" : "light";
  return theme;
}
